import { createAsyncThunk } from '@reduxjs/toolkit';

import { navigate } from '../../helpers/history';
import { clearChannelsFromSetup } from '../../components/teammates/views/Channels/helpers/fromSetupSession';

const POLL_INTERVAL_MS = 4000;
const MAX_POLL_ATTEMPTS = 45;

const delay = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

async function pollUntilSampleDataReady({ sdk, Logger }) {
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
    let status;
    try {
      const { data } = await sdk.setup.getSampleDataStatus();
      status = data?.status;
    } catch (error) {
      Logger.error(
        error instanceof Error ? error : new Error('Sample data status poll failed'),
        { error },
        ['ExploreWithSampleDataPollFailed', {
          errorMessage: (error && error.message) || 'NoMessage',
        }],
      );
      // eslint-disable-next-line no-await-in-loop
      await delay(POLL_INTERVAL_MS);
      // eslint-disable-next-line no-continue
      continue;
    }

    if (status === 'complete') {
      return status;
    }
    if (status === 'failed') {
      throw new Error('Sample data build failed');
    }

    // eslint-disable-next-line no-await-in-loop
    await delay(POLL_INTERVAL_MS);
  }

  throw new Error('Sample data build timed out');
}

/** Seed sample catalog (setup Option 3), wait until ready, then open products. */
export const exploreWithSampleData = createAsyncThunk(
  'setupSampleData/exploreWithSampleData',
  async (_, {
    extra: {
      sdk,
      Logger,
      Toast,
      localization: l18n,
    },
  }) => {
    Toast(l18n.TEAMMATES.channelsSkipSetupDialog.buildingToast);

    try {
      await sdk.setup.bringProductsIntoPim({ action: 'sampleData' });
      await pollUntilSampleDataReady({ sdk, Logger });
      clearChannelsFromSetup();
      navigate('/products');
    } catch (error) {
      Logger.error(
        error instanceof Error ? error : new Error('Explore with sample data failed'),
        { error },
        ['ExploreWithSampleDataFailed', {
          errorMessage: (error && error.message) || 'NoMessage',
        }],
      );
      Toast(l18n.TEAMMATES.channelsSkipSetupDialog.failedToast);
      throw error;
    }
  },
  {
    condition: (_, { getState }) => (
      getState().setupSampleData?.status !== 'pending'
    ),
  },
);
